import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useToaster } from "../../contexts/ToasterContext";
import { useClientStatus } from "../../contexts/ClientStatusContext";
import { clientApi } from "../../services/clientApi";
import images from '../../constants/images';
import { FiAlertCircle } from "react-icons/fi";

const validationSchema = Yup.object().shape({
  publicKey: Yup.string()
    .min(100, "Public key must be at least 100 characters long")
    .max(1000, "Public key must not exceed 1000 characters")
    .required("Public key is required"),
});

const OnboardingPage = () => {
  const [isVerifying, setIsVerifying] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToaster();
  const { markOnboardingCompleted } = useClientStatus();

  const initialValues = {
    publicKey: "",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setIsVerifying(true);
      
      const trimmedPublicKey = values.publicKey.trim();
      const response = await clientApi.verifyPublicKey({ publicKey: trimmedPublicKey });

      if (response.status === 200) {
        showToast("Public key verified successfully!", "success");
        localStorage.setItem("verifiedPublicKey", trimmedPublicKey);
        markOnboardingCompleted();
        setTimeout(() => {
          navigate("/dashboard");
        }, 500);
      } else {
        showToast(response.data.message || "Public key verification failed", "error");
        setSubmitting(false);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Invalid public key, please enter the correct one";
      showToast(errorMessage, "error");
      setSubmitting(false);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="flex items-center justify-center bg-[#F4F0FD] text-black px-4 sm:px-6 md:px-8">
      <div className="group hover:border-2 hover:border-[#C0ACF3] transition-all duration-100 max-w-[450px] bg-white p-6 sm:p-8 md:p-10 rounded-2xl shadow-xl border border-[#C0ACF3]">

        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2">
            <img src={images.stackguard} alt="Logo" className="h-8 w-8" />
            <span className="text-[24px] font-[400] font-inter text-black">StackGuard</span>
          </div>
        </div>

        <h2
          className="relative text-[30px] font-[500] mb-2 text-center font-inter text-black transition-all duration-300 delay-100 
           after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full 
           after:scale-x-0 after:origin-left after:transition-transform after:duration-300 after:delay-150 
           group-hover:after:scale-x-100 after:bg-[linear-gradient(90deg,_#272DFF_0%,_rgba(54,_51,_89,_0.3)_100%)]"
        >
          Verify Your Public Key
        </h2>

        <p className="text-[12px] font-[400] text-[#8D82AA] text-center mb-6 font-inter">
          To get started, please provide your public key for verification.
        </p>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm text-black" htmlFor="publicKey">
                    Public Key
                  </label>
                  <ErrorMessage name="publicKey">
                    {(msg) => (
                      <div className="text-red-400 text-xs flex items-center">
                        <FiAlertCircle className="mr-1" /> {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
                <Field
                  name="publicKey"
                  type="text"
                  placeholder="Enter your public key"
                  className={`w-full px-4 py-2 rounded-md bg-[#F4F0FD] text-black border ${
                    errors.publicKey && touched.publicKey
                      ? "border-red-500"
                      : "border-[#C0ACF3]"
                  } focus:outline-none focus:ring-2 focus:ring-[#C0ACF3]`}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || isVerifying}
                className={`w-full bg-[#200A57] hover:bg-[#441A9A] text-white font-semibold py-2 rounded-md transition-colors border border-[#200A57] shadow ${
                  isSubmitting || isVerifying ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {isVerifying ? "Verifying" : "Verify Public Key"}
              </button>
            </Form>
          )}
        </Formik>

        <div className="text-center mt-4">
          <button
            onClick={() => {
              localStorage.setItem("verifiedPublicKey", "skipped");
              markOnboardingCompleted();
              navigate("/dashboard");
            }}
            className="text-sm text-[#8D82AA] hover:text-[#200A57] underline cursor-pointer transition-colors"
          >
            Skip Onboarding
          </button>
        </div>
        <div className="flex justify-center my-2">
        </div>

        <div className="text-center">
          <p className="text-sm text-[#8D82AA]">
            Don’t have a public key?{" "}
            Contact your administrator
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;